(() => {
  if (window.modishProductStickyInitialized) return;
  window.modishProductStickyInitialized = true;

  const rootSelector = '[data-modish-product-sticky]';
  const desktopQuery = '(min-width: 1024px)';
  const instances = new WeakMap();

  class ProductInfoSticky {
    constructor(root) {
      this.root = root;
      this.media = root.querySelector('.main_product_media');
      this.info = root.querySelector('.main_product_info_inner');
      this.summaries = [];
      this.mediaQuery = window.matchMedia(desktopQuery);
      this.frame = null;

      if (!this.media || !this.info) return;

      this.scheduleUpdate = this.scheduleUpdate.bind(this);
      this.onSummaryClick = this.onSummaryClick.bind(this);
      if (typeof ResizeObserver === 'function') {
        this.resizeObserver = new ResizeObserver(this.scheduleUpdate);
        this.resizeObserver.observe(this.media);
        this.resizeObserver.observe(this.info);
      }
      window.addEventListener('resize', this.scheduleUpdate, { passive: true });
      this.mediaQuery.addEventListener('change', this.scheduleUpdate);
      this.summaries = [...this.info.querySelectorAll('details > summary')];
      this.summaries.forEach((summary) => summary.addEventListener('click', this.onSummaryClick));
      this.scheduleUpdate();
    }

    scheduleUpdate() {
      if (this.frame) window.cancelAnimationFrame(this.frame);
      this.frame = window.requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    update() {
      const isDesktop = this.mediaQuery.matches;
      const stickyEnabled = this.root.classList.contains('product-info-sticky');
      const mediaHeight = this.media.getBoundingClientRect().height;
      const infoHeight = this.info.getBoundingClientRect().height;
      const hasLongerMediaColumn = mediaHeight > infoHeight + 1;

      if (!isDesktop || !stickyEnabled || !hasLongerMediaColumn) {
        this.info.dataset.modishStickyState = 'inactive';
        this.info.style.removeProperty('--modish-product-info-sticky-top');
        return;
      }

      const rootStyles = window.getComputedStyle(document.documentElement);
      const rem = Number.parseFloat(rootStyles.fontSize) || 16;
      const headerHeight = Number.parseFloat(rootStyles.getPropertyValue('--header-height')) || 84;
      const previewBar = document.querySelector('#preview-bar-iframe, iframe[src*="preview_bar"]');
      const previewBarHeight = previewBar?.getBoundingClientRect().height || 0;
      const topOffset = headerHeight + (3 * rem);
      const bottomClearance = Math.max(1.5 * rem, previewBarHeight + (0.75 * rem));
      const bottomAlignedTop = window.innerHeight - infoHeight - bottomClearance;

      this.info.style.setProperty(
        '--modish-product-info-sticky-top',
        `${Math.round(Math.min(topOffset, bottomAlignedTop) * 100) / 100}px`,
      );
      this.info.dataset.modishStickyState = 'active';
    }

    onSummaryClick(event) {
      const summary = event.currentTarget;
      const details = summary?.closest('details');
      if (!summary || !details) return;

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (!details.open || !this.mediaQuery.matches) return;
          this.update();

          const rootStyles = window.getComputedStyle(document.documentElement);
          const rem = Number.parseFloat(rootStyles.fontSize) || 16;
          const headerHeight = Number.parseFloat(rootStyles.getPropertyValue('--header-height')) || 84;
          const visibleTop = headerHeight + rem;

          if (summary.getBoundingClientRect().top >= visibleTop) return;

          const infoColumn = this.info.parentElement;
          const infoColumnStyles = window.getComputedStyle(infoColumn);
          const infoColumnTop = window.scrollY + infoColumn.getBoundingClientRect().top;
          const naturalInfoTop = infoColumnTop
            + (Number.parseFloat(infoColumnStyles.paddingTop) || 0);
          const summaryOffset = summary.getBoundingClientRect().top
            - this.info.getBoundingClientRect().top;
          const documentTop = naturalInfoTop + summaryOffset;
          window.scrollTo(0, Math.max(0, documentTop - visibleTop));
          this.scheduleUpdate();
        });
      });
    }

    destroy() {
      if (!this.info) return;
      if (this.frame) window.cancelAnimationFrame(this.frame);
      this.resizeObserver?.disconnect();
      window.removeEventListener('resize', this.scheduleUpdate);
      this.mediaQuery.removeEventListener('change', this.scheduleUpdate);
      this.summaries.forEach((summary) => summary.removeEventListener('click', this.onSummaryClick));
      this.info.style.removeProperty('--modish-product-info-sticky-top');
      this.info.dataset.modishStickyState = 'inactive';
    }
  }

  const rootsWithin = (scope) => {
    const roots = [];
    if (scope.matches?.(rootSelector)) roots.push(scope);
    roots.push(...scope.querySelectorAll?.(rootSelector) || []);
    return roots;
  };

  const initialize = (scope = document) => {
    rootsWithin(scope).forEach((root) => {
      if (instances.has(root)) return;
      const instance = new ProductInfoSticky(root);
      instances.set(root, instance);
    });
  };

  const destroy = (scope) => {
    rootsWithin(scope).forEach((root) => {
      instances.get(root)?.destroy();
      instances.delete(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
